import {useContext, useEffect} from "react";
import {UNSAFE_DataRouterContext, useBlocker} from "react-router-dom";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";

type UnsavedChangesGuardProps = {
    /** Leaving would lose edits. */
    when: boolean;
    title: string;
    body: string;
    keepLabel: string;
    discardLabel: string;
};

/**
 * Asks before any route change (links, the sidebar, history navigation, programmatic navigation) while `when` holds,
 * and has the browser warn before the tab closes. Route blocking needs a data router; under a plain router (as in
 * isolated component tests) only the tab-close warning applies.
 */
export function UnsavedChangesGuard(props: UnsavedChangesGuardProps) {
    const inDataRouter = useContext(UNSAFE_DataRouterContext) !== null;

    useEffect(() => {
        if (!props.when) return;
        const warn = (event: BeforeUnloadEvent) => event.preventDefault();
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [props.when]);

    return inDataRouter ? <RouteBlockingDialog {...props} /> : null;
}

function RouteBlockingDialog({when, title, body, keepLabel, discardLabel}: UnsavedChangesGuardProps) {
    // Staying on the same path (e.g. a query or hash change) loses nothing, so only a different path is blocked.
    const blocker = useBlocker(({currentLocation, nextLocation}) => when && currentLocation.pathname !== nextLocation.pathname);
    const open = blocker.state === "blocked";
    return (
            <Dialog open={open} onClose={() => blocker.reset?.()} maxWidth="xs" fullWidth>
                <DialogTitle>{title}</DialogTitle>
                <DialogContent>
                    <Typography>{body}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => blocker.reset?.()}>{keepLabel}</Button>
                    <Button color="error" variant="contained" onClick={() => blocker.proceed?.()}>{discardLabel}</Button>
                </DialogActions>
            </Dialog>
    );
}
